import type { FC } from 'react';
import { useMemo } from 'react';
import { HStack, Radio, VStack, useRadioGroup } from '@chakra-ui/react';
import { defineMessages, useIntl } from 'react-intl';
import type { MessageDescriptor } from 'react-intl';
import { Rating } from '../../../../../ui-components/rating/Rating';
import { Text } from '../../../../../ui-components/typography';
import type { RangeFilter, UseSearchResult } from '../../../hooks/use-search';

const messages = defineMessages({
    threeAndUp: {
        id: 'search.filters.ratings.3andUp',
        defaultMessage: '3 & Up',
    },
    fourAndUp: {
        id: 'search.filters.ratings.4andUp',
        defaultMessage: '4 & Up',
    },
    five: {
        id: 'search.filters.ratings.5',
        defaultMessage: '5',
    },
});

const ratingCountMap: Record<string, MessageDescriptor> = {
    '3': messages.threeAndUp,
    '4': messages.fourAndUp,
    '5': messages.five,
};

export const RatingsFilterSection: FC<{ search: UseSearchResult }> = ({ search }) => {
    const intl = useIntl();
    const { filters, onChangeRangeFilter, isFilterSelected } = search;

    const ratingRanges = useMemo(
        () =>
            (filters?.byRange.rating || [])
                .filter((range: RangeFilter) => typeof range.start === 'number' && range.start >= 3)
                .map((range: RangeFilter) => ({
                    ...range,
                    // recalculate count based on the current range that will include the current count plus the count of the higher ranges
                    count:
                        filters?.byRange.rating
                            ?.filter((r: RangeFilter) => r.start >= range.start)
                            ?.reduce((acc, r) => acc + r.count, 0) || 0,
                })),
        [filters?.byRange.rating],
    );

    const selectedValue = useMemo(
        () =>
            ratingRanges.find((range: RangeFilter) =>
                isFilterSelected('rating', `${range.start}-*`),
            ),
        [ratingRanges, isFilterSelected],
    );

    const { getRootProps, getRadioProps } = useRadioGroup({
        name: 'rating',
        value: selectedValue ? `${selectedValue.start}-*` : '',
        onChange: (value) => {
            onChangeRangeFilter('rating', value);
        },
    });

    return (
        <VStack align="stretch" spacing={4} {...getRootProps()}>
            {ratingRanges.map((range: RangeFilter) => {
                const value = `${range.start}-*`;
                return (
                    <Radio
                        key={value}
                        {...getRadioProps({ value, isDisabled: range.count === 0 })}
                        data-testid={`rating-radio-${range.start}`}
                    >
                        <HStack spacing={2} opacity={range.count === 0 ? 0.5 : 1}>
                            <Rating
                                initialRating={Number(range.start)}
                                iconSize="sm"
                                disabled
                                cursor="pointer"
                            />
                            <Text>
                                {intl.formatMessage(ratingCountMap[range.start.toString()])}
                            </Text>
                        </HStack>
                    </Radio>
                );
            })}
        </VStack>
    );
};
